import { SectionHeader } from "@/components/common/section-components/SectionHeader";
import { Flex } from "@chakra-ui/react";
import { zodResolver } from "@hookform/resolvers/zod";
import { FieldValues, useForm, useWatch } from "react-hook-form";
import {
  ActiveDirectoryUser,
  BOUser,
  Rol,
  roleAssignationSchema,
} from "../schema/role-assignation.schema";
import { useEffect, useState } from "react";
import { UserRolesCard } from "@/components/common/section-components/card/UserRolesCard";
import { UserSelectionCard } from "@/components/common/section-components/card/UserSelectionCard";
import { useNavigate, useParams } from "react-router-dom";
import { useRoles } from "@/hooks/useRoles";
import {
  getActiveDirectoryUsers,
  getUserDetail,
} from "@/services/users-bo.service";
import { AppRoutes } from "@/constants/routes";
import {
  handleUpdateRoleAssignation,
  sucursalSelectionHandler,
} from "../schema/handler-functions";
import { getSucursales, Sucursal } from "@/services/accounts.service";

export const UserRoleAssignUpdatePage = () => {
  const [isActive, setIsActive] = useState<boolean>(false);
  useEffect(() => {
    setIsActive(true);
  }, []);
  const navigate = useNavigate();
  const { id } = useParams();

  const [currentUser, setCurrentUser] = useState<BOUser | undefined>();
  const [loadingUser, setLoadingUser] = useState<boolean>(false);

  const [loadingQueries, setLoadingQueries] = useState<boolean>(false);

  const [rolesToAssign, setRolesToAssign] = useState<Rol[]>([]);
  const { fetchAllRoles } = useRoles();

  const fetchRolesToAssign = async () => {
    const response = await fetchAllRoles();

    setRolesToAssign(response?.listado);
  };
  const {
    control,
    formState: { errors },
    setValue,
    handleSubmit,
  } = useForm({
    resolver: zodResolver(roleAssignationSchema),
  });

  const [ADUsers, setADUsers] = useState<
    ActiveDirectoryUser[] | undefined | null
  >([]);
  const [sucursales, setSucursales] = useState<Sucursal[] | null>([]);
  const [loadingSucursales, setLoadingSucursales] = useState<boolean>(false);

  const [roleSelection, user, sucursalesSelection] = useWatch({
    control,
    name: ["roles", "user", "sucursales"],
  });

  const fetchSucursales = async () => {
    setLoadingSucursales(true);

    const response = await getSucursales({
      all: true
    });
    setSucursales(response);

    setLoadingSucursales(false);
  };

  const fetchADUsers = async () => {
    const users = await getActiveDirectoryUsers({});
    setADUsers(users?.filter((user) => user?.displayName !== "unknown") ?? []);
  };

  const fetchUserData = async (id: string) => {
    setLoadingUser(true);
    const response = await getUserDetail({
      Usuario: id,
    });
    if (response) {
      setCurrentUser(response);
    }
    setLoadingUser(false);
  };

  useEffect(() => {
    if (isActive && id) {
      setLoadingQueries(true);
      fetchRolesToAssign();
      fetchUserData(String(id));
      fetchADUsers();
      fetchSucursales();
      setLoadingQueries(false);
    }
  }, [isActive, id]);

  useEffect(() => {
    if (currentUser && ADUsers) {
      setValue("user", [currentUser.usuario]);
      setValue(
        "roles",
        currentUser.roles?.map((el) => el?.id),
      );
      setValue("full_name", [currentUser?.nombreApellido]);
      setValue(
        "sucursales",
        currentUser?.sucursales?.map((el) => el?.clave),
      );
    }
  }, [currentUser, ADUsers]);

  const setRoleSelection = (value?: number[]) => {
    setValue("roles", value ?? []);
  };

  const { fetchPermissions } = useRoles();

  const onSubmit = async (event: FieldValues) => {
    await handleUpdateRoleAssignation({
      data: event,
      fetchPermissions,
      navigate,
      setLoadingUser,
      redirectPath: AppRoutes.USER_ROLES,
    });
  };

  const handleCancel = () => {
    navigate(-1);
  };

  const [isGeneralSelected, setIsGeneralSelected] = useState(false);

  const handleSucursalSelection = (selectedIds: string[]) => {
    sucursalSelectionHandler({
      isGeneralSelected,
      selectedIds,
      setIsGeneralSelected,
      setValue,
      sucursales,
    });
  };

  return (
    <Flex
      flexDir="column"
      paddingTop="20px"
      gap="30px"
      data-testid="user-role-assign-update-page"
    >
      <SectionHeader
        title="Asignación de roles y sucursales"
        cancelButtonLabel="Cancelar"
        addButtonLabel="Confirmar"
        disableDelete={true}
        handleAdd={handleSubmit(onSubmit)}
        handleCancel={handleCancel}
        disableAdd={
          !roleSelection?.length ||
          !user?.length ||
          !sucursalesSelection?.length
        }
        loading={loadingUser || loadingQueries}
      />

      <UserSelectionCard
        setValue={setValue}
        setLoadingADUsers={setLoadingQueries}
        handleSucursalSelection={handleSucursalSelection}
        ADUsers={ADUsers}
        control={control}
        errors={errors}
        noUser
        loadingAD={loadingQueries}
        loadingSucursales={loadingSucursales}
        loadingOperation={loadingUser}
        sucursales={sucursales}
      />

      <UserRolesCard
        roleSelection={roleSelection || []}
        setRoleSelection={setRoleSelection}
        userRoles={rolesToAssign}
      />
    </Flex>
  );
};
